// third party imports
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// internal rebet imports
import "./stylesTransactionTable.css";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { handleExpiredTokenError } from "../../../../utils/ErrorUtils";
import { formatLargeNumbers } from "../../../../utils/NumberFormatter";

// component imports
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import TransactionRow from "./TransactionRow/TransactionRow";

// asset imports
import SearchIcon from "../../../../assets/icons/ic_search.png";
import { Input, Pagination, Popover, Select, Table } from "antd";
import SaveButton from "../../../CommonComponents/SaveButton/SaveButton";
import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import { KPI_CARD_BACKGROUND } from "../../../../utils/Constants";
import KpiCard from "../../../CommonComponents/Dashboard/KpiCard/KpiCard";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

const TRANSACTION_METHODS = [
  {
    label: "Free Pick",
    value: "Free Pick Obtained",
  },
  {
    label: "Cash Withdraw",
    value: "Cash Withdraw",
  },
  {
    label: "Cash Deposit",
    value: "Cash Deposit",
  },
];

const TransactionTable = ({ userData }) => {
  // non-state variables
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );
  const navigate = useNavigate();

  // state variables
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState();
  const [isApiRunning, setIsApiRunning] = useState(false);
  const [transactions, setTransactions] = useState([]);

  const todayRange = [dayjs().startOf("day"), dayjs().endOf("day")];
  const [transactionDates, setTransactionDates] = useState(null);
  const [summary, setSummary] = useState({
    total_purchased: 0,
    total_withdrawn: 0,
  });
  const [transactionMethod, setTransactionMethod] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [resetTable, setResetTable] = useState(false);
  const [betsCache, setBetsCache] = useState({});

  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 20,
    total_results: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });

  const formatTimestamp = (timestampStr) => {
    dayjs.extend(utc);

    const timestamp = Number(timestampStr);
    return dayjs.unix(timestamp).utc().format("DD MMMM YYYY, HH:mm:ss");
  };

  const columns = [
    {
      title: "Date / Time",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (text) => {
        return <p className="timestamp-text">{formatTimestamp(text)}</p>;
      },
      width: 150,
    },
    {
      title: "ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 250,
    },
    {
      title: "Money Type",
      dataIndex: "wallet_type",
      key: "wallet_type",
      width: 150,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      render: (text) => {
        return formatLargeNumbers(text, 2);
      },
    },
    {
      title: "Type",
      dataIndex: "transaction_type",
      key: "transaction_type",
      width: 100,
    },
    {
      title: "Transaction Method",
      dataIndex: "transaction_method",
      key: "transaction_method",
      width: 200,
      render: (text, record) => {
        return (
          <p
            className={`transaction-method-text ${
              record?.by_admin ? "by-admin" : "not-by-admin"
            }`}
          >
            {handleTransactionMethod(record)}
          </p>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 150,
      render: (text, record) => {
        return record?.by_admin ? (
          <Popover content={text} placement="topLeft">
            <p className="reason-text ellipsis">{text || "-"}</p>
          </Popover>
        ) : (
          <p className="reason-text ellipsis">-</p>
        );
      },
    },
    {
      title: "Balance",
      dataIndex: "total_balance",
      key: "total_balance",
      render: (text) => {
        return formatLargeNumbers(text, 2);
      },
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (text, record) => {
        return (
          <p className={`status-text ${record?.status.toLowerCase()}`}>
            {record?.status}
          </p>
        );
      },
    },
  ];

  const handleTransactionMethod = (record) => {
    const byAdmin = record?.by_admin;

    if (byAdmin) {
      return "Manual Add";
    } else {
      return record?.title;
    }
  };

  console.log("cache", betsCache);

  // use effects
  useEffect(() => {
    getUsersTransactions(1, true, todayRange, false, true, null);
  }, [userData]);

  // api functions
  const getUsersTransactions = async (
    page = 1,
    refresh = false,
    queryString = todayRange,
    isQueryString = true,
    allTime = false,
    lastEvaluatedKey = null
  ) => {
    setIsApiRunning(true);

    let startDate = "";
    let endDate = "";

    if (isQueryString) {
      let formattedQueryString = queryString.split("&");
      startDate = dayjs(formattedQueryString[0]).format("YYYY-MM-DD");
      endDate = dayjs(formattedQueryString[1]).format("YYYY-MM-DD");
    } else if (allTime) {
      startDate = null;
      endDate = null;
    } else {
      startDate = queryString[0]?.format("YYYY-MM-DD");
      endDate = queryString[1]?.format("YYYY-MM-DD");
    }

    const cacheKey = page;

    if (betsCache[cacheKey] && !refresh) {
      const cached = betsCache[cacheKey];
      setTransactions(cached.all_bets);
      setPagination(cached.pagination);
      setIsApiRunning(false);
      return;
    }

    let params = {
      page: page,
      user_id: userData?.user_id,
      start_date: startDate,
      end_date: endDate,
      last_evaluated_key: lastEvaluatedKey,
    };

    if (searchValue && !refresh) {
      params.transaction_id = searchValue;
    }

    if (transactionMethod && !refresh) {
      params.transaction_method = transactionMethod;
    }

    console.log("params", params);

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GET_TRANSACTION_HISTORY,
        params
      );

      console.log("response", response);
      const response_data = response?.data?.data;

      console.log("response_data", response_data);

      const all_transactions = Array.isArray(response?.data?.data?.items)
        ? [...response.data.data.items]
        : [];

      const all_transactions_sorted = all_transactions?.sort((a, b) => {
        return parseFloat(b.timestamp) - parseFloat(a.timestamp);
      });
      const all_unique_transactions_sorted = Array.from(
        new Map(
          all_transactions_sorted.map((transaction) => [
            transaction.transaction_id,
            transaction,
          ])
        ).values()
      );

      const lastEvaluatedKey = response?.data?.data?.last_evaluated_key;
      const hasNext = lastEvaluatedKey ? true : false;
      const currentPage = page;
      const hasPrev = currentPage > 1 ? true : false;
      const totalResults = lastEvaluatedKey
        ? page * 20 + 1
        : page > 1 && lastEvaluatedKey
        ? page * 20 + 1
        : page * 20;

      setTransactions(all_unique_transactions_sorted);
      setLastEvaluatedKey(lastEvaluatedKey);
      setSummary({
        total_purchased: response_data?.total_purchased,
        total_withdrawn: response_data?.total_withdrawn,
      });

      const paginationData = {
        current_page: currentPage,
        page_size: 20,
        total_results: totalResults,
        total_pages: response?.data?.data?.pagination?.total_pages,
        has_next: hasNext,
        has_prev: hasPrev,
      };

      setPagination(paginationData);

      setTransactions(all_unique_transactions_sorted);

      setBetsCache((prev) => ({
        ...prev,
        [cacheKey]: {
          all_bets: all_unique_transactions_sorted,
          summary,
          pagination: paginationData,
        },
      }));

      setIsApiRunning(false);
    } catch (error) {
      console.log("error fetching user transaction history", error);
      setIsApiRunning(false);
      handleExpiredTokenError(navigate, error?.response);
    }
  };

  const getUsersTransactionsById = async () => {
    if (searchValue === "" && transactionMethod === null) {
      return;
    }

    setIsApiRunning(true);

    const params = { user_id: userData?.user_id };

    if (searchValue) {
      params.transaction_id = searchValue;
    }

    if (transactionMethod) {
      params.transaction_method = transactionMethod;
    }

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GET_TRANSACTION_HISTORY,
        params
      );

      const all_transactions = [...response?.data?.data?.items];

      const all_transactions_sorted = all_transactions?.sort((a, b) => {
        return parseFloat(b.timestamp) - parseFloat(a.timestamp);
      });

      const all_unique_transactions_sorted = Array.from(
        new Map(
          all_transactions_sorted.map((transaction) => [
            transaction.transaction_id,
            transaction,
          ])
        ).values()
      );

      setTransactions(all_unique_transactions_sorted);
      setLastEvaluatedKey(response?.data?.data?.last_evaluated_key);
      setResetTable(true);

      setIsApiRunning(false);
    } catch (error) {
      console.log("error fetching user transaction history", error?.response);
      setIsApiRunning(false);
      handleExpiredTokenError(navigate, error?.response);
    }
  };
  // helper functions

  const handleDateChange = (queryString) => {
    getUsersTransactions(1, true, queryString, true, false, null);
  };

  const handleChangeTransactionMethod = (value) => {
    setTransactionMethod(value);
  };

  const handleSearchValueChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleResetTable = () => {
    getUsersTransactions(1, true, todayRange, false, true, null);
    setSearchValue("");
    setTransactionMethod(null);
    setTransactionDates(null);
    setResetTable(false);
  };

  const handlePaginationChange = (page) => {
    let allTimeParams = transactionDates ? false : true;
    let queryStringParams = transactionDates ? transactionDates : todayRange;

    console.log("page", page);
    console.log("queryStringParams", queryStringParams);
    console.log("allTimeParams", allTimeParams);

    getUsersTransactions(
      page,
      false,
      queryStringParams,
      false,
      allTimeParams,
      lastEvaluatedKey
    );
  };

  return (
    <div className="transactions-user-wallet-container">
      <div className="transactions-user-wallet-header">
        <div className="kpi-cards-container">
          <KpiCard
            isApiRunning={isApiRunning}
            title="Total Purchased"
            value={summary?.total_purchased}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isApiRunning}
            title="Total Withdrawn"
            value={summary?.total_withdrawn}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
        <CustomDatePicker
          getData={handleDateChange}
          dates={transactionDates}
          setDates={setTransactionDates}
          showPresets={true}
          startRange="Today"
        />
      </div>
      <div className="transactions-user-wallet-table-container">
        <div className="head-controls">
          <div className="head-controls transactions-user-wallet-table-header-container">
            <div
              className="header-text-container"
              style={{ width: "max-content" }}
            >
              <p className="header-text">{userData?.username}'s Transactions</p>
            </div>
            <div className="transactions-user-wallet-search-container">
              <Input
                type="text"
                placeholder="Search transactions..."
                className="bet-searchInput"
                value={searchValue}
                onChange={handleSearchValueChange}
              />
              <div className="drop-down-container">
                <div className="casino-selector">
                  <Select
                    placeholder="Transaction Method"
                    options={Object.values(TRANSACTION_METHODS)}
                    value={transactionMethod}
                    onChange={handleChangeTransactionMethod}
                  />
                </div>
              </div>
              <button
                className="search-button"
                style={{ margin: "0px" }}
                onClick={getUsersTransactionsById}
              >
                <img
                  className="search-icon"
                  src={SearchIcon}
                  alt="search-icon"
                />
              </button>
            </div>
          </div>
        </div>

        <div className="transactions-table-container">
          {isApiRunning ? (
            <RebetLoader height={"100px"} width={"100px"} />
          ) : (
            <Table
              columns={columns}
              dataSource={transactions}
              pagination={false}
              scroll={{ y: 400, x: 1300 }}
              rowKey={(record) => record?.transaction_id}
            />
          )}
        </div>

        {!isApiRunning && (pagination?.has_next || pagination?.has_prev) && (
          <div className="transactions-pagination-container">
            <Pagination
              current={pagination.current_page}
              total={pagination.total_results}
              pageSize={pagination.page_size}
              showSizeChanger={false}
              onChange={(page) => {
                if (
                  page === pagination.current_page - 1 ||
                  page === pagination.current_page + 1
                ) {
                  handlePaginationChange(page);
                }
              }}
              itemRender={(page, type, originalElement) => {
                if (type === "page") {
                  return (
                    <a
                      rel="nofollow"
                      disabled={page > pagination.current_page + 1}
                      className={`${
                        page > pagination.current_page + 1 ? "disabled" : ""
                      }`}
                    >
                      {page}
                    </a>
                  );
                }
                return originalElement;
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionTable;
